import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { 
    LibraryIcon, CheckBadgeIcon, CalendarIcon, 
    TrendingUpIcon, Favicon, AcademicCapIcon, CashIcon, ChatAltIcon, ComputerDesktopIcon
} from '../../components/icons/Icons';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// --- 1. LOCAL ICONS ---
const CurrencyDollarIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const CreditCardIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
    </svg>
);

// --- 2. HOLIDAY BANNER ---
function HolidayBanner({ appId, navigateTo, userLocation }) {
    const [nextHoliday, setNextHoliday] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!appId) return;
        
        const q = query(
            collection(db, `/artifacts/${appId}/public/data/holidays`),
            where('date', '>=', new Date()),
            orderBy('date', 'asc'),
            limit(5) 
        );

        const unsub = onSnapshot(q, (snapshot) => {
            if (!snapshot.empty) {
                const holidays = snapshot.docs.map(doc => doc.data());
                
                // Location Logic
                const uLoc = userLocation ? userLocation.toLowerCase().trim() : '';
                let targetLocation = 'mumbai'; 
                if (uLoc === 'delhi' || uLoc === 'new delhi') targetLocation = 'delhi';
                if (uLoc === 'bangalore' || uLoc === 'bengaluru') targetLocation = 'bangalore';

                const match = holidays.find(h => {
                    const hLoc = h.location ? h.location.toLowerCase().trim() : 'all';
                    return hLoc === 'all' || hLoc === targetLocation;
                });

                if (match) {
                    setNextHoliday({
                        ...match,
                        dateStr: match.date.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    });
                }
            }
            setLoading(false);
        });
        return () => unsub();
    }, [appId, userLocation]);

    if (loading || !nextHoliday) return null;

    return (
        <button 
            onClick={() => navigateTo('hr', 'holidays')}
            className="hidden md:flex items-center bg-blue-50 hover:bg-blue-100 text-[#213368] px-4 py-2 rounded-full text-sm font-medium transition-colors border border-blue-100 shadow-sm"
        >
            <CalendarIcon className="h-4 w-4 mr-2 text-[#20BEC6]" />
            <span className="opacity-75 mr-1 text-xs uppercase tracking-wide font-bold">Up Next:</span>
            <span className="font-bold mr-1">{nextHoliday.dateStr}</span>
            <span className="mx-1 text-gray-400">|</span>
            <span className="truncate max-w-[200px]">{nextHoliday.holidayName}</span>
            {nextHoliday.location && nextHoliday.location.toLowerCase() !== 'all' && (
                <span className="ml-2 text-[10px] bg-white px-1.5 rounded border border-blue-100 text-gray-500 capitalize">
                    {nextHoliday.location}
                </span>
            )}
        </button>
    );
}

// --- 3. SERVICE CARD ---
function ServiceCard({ onClick, icon: Icon, title, description, themeColor }) {
    const styles = {
        teal:   "border-l-[#20BEC6] group-hover:bg-[#20BEC6]/5",
        orange: "border-l-[#F4A866] group-hover:bg-[#F4A866]/5",
        red:    "border-l-[#D95448] group-hover:bg-[#D95448]/5",
        blue:   "border-l-[#213368] group-hover:bg-[#213368]/5",
    };

    const iconColors = {
        teal: "text-[#20BEC6]", orange: "text-[#F4A866]", 
        red: "text-[#D95448]", blue: "text-[#213368]"
    };

    return (
        <button
            onClick={onClick}
            className={`group relative flex flex-col justify-between p-5 bg-white/95 backdrop-blur-sm rounded-lg shadow-sm hover:shadow-md transition-all duration-200 border border-gray-200 border-l-4 ${styles[themeColor]} h-40 w-full text-left`}
        >
            <div className={`p-2 w-fit rounded-md bg-gray-50 ${iconColors[themeColor]}`}>
                <Icon className="h-6 w-6" />
            </div>
            <div>
                <h3 className="text-base font-bold text-[#213368] mb-1">{title}</h3>
                <p className="text-xs text-gray-500 leading-snug line-clamp-2">{description}</p>
            </div>
        </button>
    );
}

// --- 4. MAIN PAGE ---
export default function HomePage({ setView, navigateTo, appId, userProfile }) {
    
    // Module Configuration
    const moduleGroups = [
        {
            title: "My Workspace",
            themeColor: "teal",
            modules: [
                { id: 'timeCoach', title: "AI Coach", desc: "Your personal time & team management assistant.", icon: Favicon },
                { 
                    id: 'peer', title: "Peer Appreciation", icon: CheckBadgeIcon,
                    desc: (<>Recognize colleagues & express gratitude.<br /><span className="text-xs font-bold text-[#20BEC6]">Module Credit: Drivyadrishti</span></>) 
                },
		{ 
                    id: 'office', 
                    title: "Office Scheduler", 
                    desc: (<>Manage hybrid schedules and WFH requests.<br /><span className="text-xs font-bold text-[#20BEC6]">Module Credit: Drivyadrishti</span></>), 
                    icon: CalendarIcon 
                }

            ]
        },
        {
            title: "Finance & Operations",
            themeColor: "orange",
            modules: [
                { id: 'expenses', title: "Expense Center", desc: "Submit reimbursements & track claims.", icon: CurrencyDollarIcon },
                { id: 'procurement', title: "Purchase Center", desc: "Raise POs, invoices & vendor requests.", icon: CreditCardIcon },
		{ 
                    id: 'it', 
                    title: "IT Center", 
                    desc: "Manage hardware inventory & assignments.", 
                    icon: ComputerDesktopIcon 
                },
                { 
                    id: 'finance', title: "Finance Center", desc: "Vendor approvals & payment controls.", icon: CashIcon,
                    show: userProfile?.role === 'Admin' || userProfile?.department === 'Finance' 
                },
                { 
                    id: 'leaves', title: "Leave Admin", desc: "Manage leave policies & balances.", icon: CalendarIcon,
                    show: userProfile?.role === 'Admin' 
                }
            ]
        },
        {
            title: "Growth & Development",
            themeColor: "red",
            modules: [
                { id: 'performance', title: "Performance", desc: "Periodic reviews, goals & appraisals.", icon: TrendingUpIcon },
                { 
                    id: 'pd', title: "Professional Development", icon: AcademicCapIcon,
                    desc: (<>Skill tracking, training & peer learning.<br /><span className="text-xs font-bold text-[#D95448]">Inspiration Credit: Drivyadrishti</span></>)
                }
            ]
        },
        {
            title: "Company Hub",
            themeColor: "blue",
            modules: [
                { id: 'hr', title: "HR Center", desc: "Policies, forms & employee directory.", icon: LibraryIcon },
                { id: 'feedback', title: "Feedback & Ideas", desc: "Share suggestions or message founders.", icon: ChatAltIcon },
            ]
        }
    ];

    // ✅ FIX: Define Hex codes here for reliability
    const themeHexCodes = {
        teal: '#20BEC6',
        orange: '#F4A866',
        red: '#D95448',
        blue: '#213368'
    };

    return (
        <div className="relative min-h-screen">
            
            {/* Fixed Background Layer (Watermark Style) */}
            <div 
                className="fixed inset-0 z-0 pointer-events-none opacity-10"
                style={{
                    backgroundImage: "url('/brand_mission.jpg')", 
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    filter: 'grayscale(20%)'
                }}
            />

            {/* Content Layer */}
            <div className="relative z-10 space-y-8 pb-12">
                
                {/* HEADER SECTION */}
                <div className="flex flex-col md:flex-row justify-between items-end md:items-center bg-white/90 backdrop-blur-sm p-6 rounded-xl border border-gray-200 shadow-sm mb-8">
                    <div>
                        <h2 className="text-2xl font-bold text-[#213368]">
                            Hello, {userProfile?.name?.split(' ')[0]} 👋
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Welcome back. Here is what's happening in your workspace today.
                        </p>
                    </div>
                    
                    {/* Location-Aware Holiday Banner */}
                    <div className="mt-4 md:mt-0">
                        <HolidayBanner 
                            appId={appId} 
                            navigateTo={navigateTo} 
                            userLocation={userProfile?.location} 
                        />
                    </div>
                </div>

                {/* MODULE GRID */}
                <div className="space-y-10">
                    {moduleGroups.map((group, gIndex) => {
                        const visibleModules = group.modules.filter(m => m.show !== false);
                        if (visibleModules.length === 0) return null;

                        return (
                            <div key={gIndex}>
                                {/* Section Header */}
                                <div className="flex items-center space-x-2 mb-4">
                                    {/* ✅ FIX: Use inline style for background color to ensure consistency */}
                                    <span 
                                        className="h-2 w-2 rounded-full" 
                                        style={{ backgroundColor: themeHexCodes[group.themeColor] }}
                                    ></span>
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">{group.title}</h3>
                                </div>

                                {/* Responsive Grid */}
                                <div className="grid gap-6 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
                                    {visibleModules.map((module) => (
                                        <ServiceCard 
                                            key={module.id}
                                            onClick={() => setView(module.id)}
                                            title={module.title}
                                            description={module.desc}
                                            icon={module.icon}
                                            themeColor={group.themeColor}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}